import _ from 'lodash';

/**
 * A cache for storing ImmutableModel instances.
 *
 * Offers both list and dictionary access to the data. Cache differentiates based on the given arguments, different
 * arguments are stored in different caches.
 *
 * Exception is the argument 'full', which is used such that if a non-full dataset is requested and a full one exists,
 * the full one is returned.
 *
 * The cache supports multiple and single lookups. Single lookups will use their own cache, but full multiple lookups
 * will also populate the single lookup cache.
 *
 * NOTE: Each stored model must have an 'id' property so it can be indexed.
 * NOTE: Full vs non-full datasets must follow the rule that non-full datasets must not have any properties that the
 * full ones do not have. This is to ensure that full ones can be used in place of non-full ones.
 */
class ImmutableModelCache {
  constructor($q, parent=null) {
    this.$q = $q;

    // Parent class (resource) that owns this cache. Used for debugging.
    this._parent = parent;

    // Storage for the cached data, indexed by the arguments and contains a dict of 'full' (bool) and the data as list
    // and dict.
    this._data = new Map();

    // Storage for single object queries, populated by them and full getMany queries. Indexed by id, value is the
    // object.
    this._singles = new Map();
  }

  /**
   * Get a collection of results from the cache filtered by args. If data does not exist in cache, getter is executed
   * and given the same args, it should return a ResourceCollection, which is then added to cache and returned.
   * @param {Object.<string, string|number|boolean>} args Arguments used for filtering.
   * @param {function(Object.<string, string|number>): Promise.<ResourceCollection>} getter Getter function that must
   *   return a promise that resolves with CacheData.
   * @returns {Promise.<ResourceCollection>} Promise that is resolved with a ResourceCollection.
   */
  getMany(args, getter) {
    const {full=false} = args;
    const originalArgs = _.clone(args);
    delete args['full'];

    const existingData = this._getData(args);

    // Return the existing data if it is available and either it is full or we are not requesting full data
    if (existingData != null && (existingData.full || !full)) {
      const {list, dict} = existingData;
      return this.$q.when({list, dict});
    }
    else {
      return getter(originalArgs).then(data => {
        // If full data already exists, don't replace it. Otherwise replace data
        if (existingData == null || !existingData.full) {
          this._data.set(args, {
            full,
            dict: data.dict,
            list: data.list
          });
        }

        // If doing a full fetch, insert data to singles cache as well if it didn't already exist
        if (full) {
          for (const obj of data.list) {
            this._singles.set(obj.id, obj);
          }
        }

        return data;
      });
    }
  }

  /**
   * Get a collection of results from the cache filtered by args. If data does not exist in cache, null is returned.
   * @param {Object.<string, string|number|boolean>} args Arguments used for filtering.
   * @returns {?ResourceCollection} ResourceCollection or null if no data
   */
  getManyCached(args) {
    return this._getData(args);
  }

  /**
   * Get all results from the cache. If data does not exist in cache, null is returned.
   * @return {?ResourceCollection}
   */
  getAllCached() {
    return this.getManyCached({});
  }

  /**
   * Get one model from the cache. If data does not exist in cache, getter is executed with the ID and should return one
   * ImmutableModel that is then added to the cache and returned.
   * @param {number} id ID of the model
   * @param {function(number): Promise.<ImmutableModel>} getter Getter function that should return a promise that
   *   resolves with the ImmutableModel.
   * @return {Promise.<ImmutableModel>}
   */
  getOne(id, getter) {
    if (this._singles.has(id)) {
      return this.$q.when(this._singles.get(id));
    }
    else {
      return getter(id).then(o => {
        this._singles.set(o.id, o);
        return o;
      });
    }
  }

  /**
   * Get one model from the cache. If the data does not exist in the cache, null is returned.
   * @param {number} id ID of the model
   * @returns {?ImmutableModel}
   */
  getOneCached(id) {
    return this._singles.has(id)? this._singles.get(id) : null;
  }

  // Get data from multiple result cache with given args. We need to iterate very uglily because of Map using
  // strict equality but it should not be a major performance issue
  _getData(args) {
    for (const key of this._data.keys()) {
      if (_.isEqual(key, args)) {
        return this._data.get(key);
      }
    }

    return null;
  }
}

export default ImmutableModelCache;
